import React from 'react'
import { useWatch } from 'react-hook-form'

import { HHFormSelectField, HHFormTextField } from 'components/form-fields/v5'
import { EXCEPTION_TYPE } from 'settings/constants/exceptionType'
import T from 'T'

import { DESCRIPTION_FIELD_LABEL, EXCEPTION_FORM_FIELD, NUMERIC_DESCRIPTION_TYPES, SPECIAL_OPTIONS } from './constants'
import ExceptionDelayField from './ExceptionDelayField'
import DamageFields from './per-type/DamageFields'
import LocationFields from './per-type/LocationFields'
import NoAccessFields from './per-type/NoAccessFields'

import type { ExceptionFormValues } from './constants'
import type { Control } from 'react-hook-form'

type ExceptionTypeFieldsProps = {
  control: Control<ExceptionFormValues>
  exceptionType?: string
}

const ExceptionTypeFields: React.FC<ExceptionTypeFieldsProps> = ({ control, exceptionType }) => {
  const watchedDescription = useWatch({ control, name: EXCEPTION_FORM_FIELD.DESCRIPTION })

  if (exceptionType === EXCEPTION_TYPE.LOCATION) return <LocationFields control={control} />
  if (exceptionType === EXCEPTION_TYPE.NO_ACCESS) return <NoAccessFields control={control} />
  if (exceptionType === EXCEPTION_TYPE.DAMAGE) return <DamageFields control={control} />
  if (exceptionType === EXCEPTION_TYPE.DELAY) return <ExceptionDelayField control={control} />
  if (exceptionType === EXCEPTION_TYPE.SPECIAL) {
    // Mobile writes free-form variants ("Dig out" vs the web's "Digout") — append the stored
    // value as an option so the select displays it instead of rendering empty.
    const isStoredSpecialUnmatched = Boolean(watchedDescription) && !SPECIAL_OPTIONS.some(option => option.value === watchedDescription)
    const specialOptions = isStoredSpecialUnmatched
      ? [...SPECIAL_OPTIONS, { label: watchedDescription, value: watchedDescription }]
      : SPECIAL_OPTIONS
    return (
      <HHFormSelectField
        fullWidth
        control={control}
        deprecatedLabel={false}
        label={T.SPECIAL_ITEM}
        name={EXCEPTION_FORM_FIELD.DESCRIPTION}
        options={specialOptions}
      />
    )
  }

  const descriptionLabel = exceptionType ? DESCRIPTION_FIELD_LABEL[exceptionType] : undefined
  if (descriptionLabel) {
    const isNumeric = Boolean(exceptionType && NUMERIC_DESCRIPTION_TYPES.has(exceptionType))
    return (
      <HHFormTextField
        fullWidth
        control={control}
        deprecatedLabel={false}
        label={descriptionLabel}
        name={EXCEPTION_FORM_FIELD.DESCRIPTION}
        type={isNumeric ? 'number' : 'text'}
      />
    )
  }

  return null
}

export default ExceptionTypeFields
